import { PaginationParams } from '@/lib/types';
import { Row, Space, Spin } from 'antd';
import { FC, useEffect, useRef, useState } from 'react';

type ScrollListProps = {
  loadMore: (page: number) => Promise<void>; // items are handled in parent
  pagination: PaginationParams | undefined;
  children: React.JSX.Element | undefined; // items are passed here, this makes implementing other things than lists (such as collapse) easier
  height?: React.CSSProperties['height'];
};

export const ScrollList: FC<ScrollListProps> = ({
  loadMore,
  pagination,
  children,
  height = '50vh',
}: // items,
ScrollListProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isScrollable, setIsScrollable] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadInProgressRef = useRef(false);

  // hook for adding incoming items & updating values
  useEffect(() => {
    setHasMore(
      pagination
        ? pagination.total > pagination.page * pagination.pageSize
        : false,
    );
  }, [pagination]);

  useEffect(() => {
    if (!loading) {
      loadInProgressRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const updateScrollability = () => {
      setIsScrollable(container.scrollHeight > container.clientHeight);
    };

    updateScrollability();

    const resizeObserver = new ResizeObserver(() => {
      updateScrollability();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    children,
    height,
    pagination?.page,
    pagination?.pageSize,
    pagination?.total,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    const sentinel = sentinelRef.current;

    if (!container || !sentinel || !hasMore || !isScrollable) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          handleLoad();
        }
      },
      {
        root: container,
        rootMargin: '0px 0px 32px 0px',
        threshold: 0,
      },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [
    hasMore,
    isScrollable,
    loading,
    pagination?.page,
    pagination?.pageSize,
    pagination?.total,
  ]);

  const handleLoad = () => {
    if (loadInProgressRef.current || loading || !hasMore || !isScrollable) {
      return;
    }

    loadInProgressRef.current = true;
    // We use our own page numbers; as it won't reset properly using the built-in page number
    const nextPage = (pagination?.page ?? 0) + 1;
    setLoading(true);
    loadMore(nextPage).finally(() => {
      setLoading(false);
    });
  };

  return (
    <>
      <div
        ref={containerRef}
        style={{
          height: height,
          overflow: 'auto',
        }}
      >
        {children}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>
      <Row justify="center" style={{ height: 20 }}>
        {loading && hasMore && isScrollable && (
          <Space>
            <Spin /> Loading more...
          </Space>
        )}
      </Row>
    </>
  );
};
